import type { ToastMessage } from '@/types'

export class Toast {
  private container: HTMLElement | null = null
  private toasts: Map<string, HTMLElement> = new Map()
  private loadingToast: HTMLElement | null = null

  init(container: HTMLElement): void {
    this.container = container
    console.log('🍞 Toast system initialized')
  }

  success(message: string, title: string = 'Success'): void {
    this.show({ type: 'success', title, message })
  }

  error(message: string, title: string = 'Error'): void {
    this.show({ type: 'error', title, message })
  }

  warning(message: string, title: string = 'Warning'): void {
    this.show({ type: 'warning', title, message })
  }

  info(message: string, title: string = 'Info'): void {
    this.show({ type: 'info', title, message })
  }

  private show(options: Omit<ToastMessage, 'id'>): void {
    if (!this.container) {
      console.warn('Toast container not initialized')
      return
    }

    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    const duration = options.duration || 5000

    const toast = this.createToastElement({
      id,
      ...options,
      duration
    })

    this.container.appendChild(toast)
    this.toasts.set(id, toast)

    // Animate in
    setTimeout(() => {
      toast.classList.add('translate-y-0', 'opacity-100')
      toast.classList.remove('-translate-y-full', 'opacity-0')
    }, 10)

    // Auto dismiss
    setTimeout(() => {
      this.dismiss(id)
    }, duration)
  }

  private createToastElement(toast: ToastMessage): HTMLElement {
    const element = document.createElement('div')
    element.id = toast.id
    
    const styles = {
      success: {
        bg: 'bg-green-50',
        border: 'border-green-200',
        icon: 'text-green-600',
        iconBg: 'bg-green-100'
      },
      error: {
        bg: 'bg-red-50',
        border: 'border-red-200', 
        icon: 'text-red-600',
        iconBg: 'bg-red-100'
      },
      warning: {
        bg: 'bg-yellow-50',
        border: 'border-yellow-200',
        icon: 'text-yellow-600',
        iconBg: 'bg-yellow-100'
      },
      info: {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        icon: 'text-blue-600',
        iconBg: 'bg-blue-100'
      }
    }

    const icons = {
      success: '✓',
      error: '✕',
      warning: '⚠',
      info: 'ℹ'
    }

    const style = styles[toast.type]
    
    element.className = `fixed top-4 right-4 -translate-y-full opacity-0 max-w-sm w-full z-[9999] transition-all duration-300 ${style.bg} ${style.border} border shadow-lg rounded-lg pointer-events-auto overflow-hidden`

    element.innerHTML = `
      <div class="p-4">
        <div class="flex items-start">
          <div class="flex-shrink-0">
            <div class="${style.iconBg} rounded-full w-8 h-8 flex items-center justify-center">
              <span class="${style.icon} text-sm font-bold">${icons[toast.type]}</span>
            </div>
          </div>
          <div class="ml-3 flex-1">
            <p class="text-sm font-semibold text-gray-900">${toast.title}</p>
            ${toast.message ? `<p class="mt-1 text-sm text-gray-600 leading-relaxed">${toast.message}</p>` : ''}
          </div>
          <div class="ml-4 flex-shrink-0">
            <button class="inline-flex text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300 rounded p-1" onclick="this.closest('[id^=toast-]').remove()">
              <span class="sr-only">Close</span>
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>
    `

    return element
  }

  private dismiss(id: string): void {
    const toast = this.toasts.get(id)
    if (toast) {
      toast.classList.add('-translate-y-full', 'opacity-0')
      toast.classList.remove('translate-y-0', 'opacity-100')
      
      setTimeout(() => {
        if (toast.parentElement) {
          toast.parentElement.removeChild(toast)
        }
        this.toasts.delete(id)
      }, 300)
    }
  }

  showLoading(title: string, message?: string): void {
    if (this.loadingToast) {
      this.hideLoading()
    }

    // Create a simple toast-style loading notification instead of a modal overlay
    const toast = document.createElement('div')
    toast.className = 'fixed top-4 right-4 z-[9999] bg-white border-3 border-gray-900 rounded-lg shadow-lg p-4 max-w-md w-full transition-all duration-300'
    toast.style.boxShadow = '6px 6px 0 #111827'
    toast.innerHTML = `
      <div class="flex items-center space-x-3">
        <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 flex-shrink-0"></div>
        <div class="flex-1">
          <h3 class="text-sm font-semibold text-gray-900">${title}</h3>
          ${message ? `<p class="text-xs text-gray-600 whitespace-pre-line mt-1">${message}</p>` : ''}
        </div>
      </div>
    `

    document.body.appendChild(toast)
    this.loadingToast = toast

    // Animate in
    setTimeout(() => {
      toast.style.opacity = '1'
      toast.style.transform = 'translate(-50%, 0)'
    }, 10)
  }

  hideLoading(): void {
    if (this.loadingToast) {
      this.loadingToast.style.opacity = '0'
      this.loadingToast.style.transform = 'translate(-50%, -20px)'
      setTimeout(() => {
        if (this.loadingToast && this.loadingToast.parentElement) {
          this.loadingToast.parentElement.removeChild(this.loadingToast)
        }
        this.loadingToast = null
      }, 300)
    }
  }

  updateLoadingMessage(title: string, message?: string): void {
    if (this.loadingToast) {
      const titleElement = this.loadingToast.querySelector('h3')
      const messageElement = this.loadingToast.querySelector('p')
      
      if (titleElement) {
        titleElement.textContent = title
      }
      
      if (messageElement) {
        messageElement.textContent = message || ''
        messageElement.style.display = message ? 'block' : 'none'
      } else if (message) {
        // Add message element if it doesn't exist
        const contentDiv = this.loadingToast.querySelector('.flex-1')
        if (contentDiv) {
          const newMessageElement = document.createElement('p')
          newMessageElement.className = 'text-xs text-gray-600 whitespace-pre-line mt-1'
          newMessageElement.textContent = message
          contentDiv.appendChild(newMessageElement)
        }
      }
    }
  }
}

// Convenience function for showing toasts
export function showToast(message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info', title?: string): void {
  const toast = new Toast()
  toast.init(document.body)
  
  switch (type) {
    case 'success':
      toast.success(message, title)
      break
    case 'error':
      toast.error(message, title)
      break
    case 'warning':
      toast.warning(message, title)
      break
    default:
      toast.info(message, title)
      break
  }
} 